/**
 * Server error parsing for Custom Forms.
 *
 * Backend value-coercion + validation errors are surfaced through
 * BeanPropertyBindingResult.reject(code) — the response has no per-error
 * `field` slot. Convention: any field-scoped error code ends with
 * `:${fieldKey}` (sometimes followed by positional info like `:${rowIdx}`).
 *
 * Examples:
 *   com.kerkton.orna.form.value.date.unparseable:startDate
 *   com.kerkton.orna.form.instance.value.required:emergencyContact
 *   com.kerkton.orna.form.instance.value.table.row.notMap:services:0
 *
 * `parseFieldKeyFromErrorCode` walks `:`-separated segments right-to-left
 * and returns the first segment that's a known field key — so positional
 * tails (e.g. `:0`) don't shadow the actual key.
 */

import type { FormSchemaVersionDetailData } from '@/services/api-types';

export interface ServerError {
  errorCode?: string;
  message?: string;
  field?: string;
}

export function extractServerErrors(err: unknown): ServerError[] {
  const e = err as Record<string, unknown> | null | undefined;
  const body = (e?.responseJSON ?? e?.body ?? e) as Record<string, unknown> | null | undefined;
  const errors = body?.errors as Record<string, unknown> | undefined;
  const embedded = errors?._embedded as Record<string, unknown> | undefined;
  const list = embedded?.errors;
  if (Array.isArray(list)) return list as ServerError[];
  if (Array.isArray(errors)) return errors as ServerError[];
  return [];
}

export function collectFieldKeys(
  schemaVersion: FormSchemaVersionDetailData | null | undefined,
): Set<string> {
  const keys = new Set<string>();
  if (!schemaVersion) return keys;
  for (const section of schemaVersion.sections ?? []) {
    for (const field of section.fields ?? []) {
      if (field.key) keys.add(field.key);
    }
  }
  return keys;
}

export function parseFieldKeyFromErrorCode(
  code: string | undefined,
  knownKeys: Set<string>,
): string | null {
  if (!code) return null;
  const parts = code.split(':');
  // Skip parts[0] (the dotted base code) and walk right-to-left.
  for (let i = parts.length - 1; i >= 1; i--) {
    if (knownKeys.has(parts[i])) return parts[i];
  }
  return null;
}

/**
 * Group structured errors into per-field messages (keyed by field key) and
 * leftover form-level errors with no field association. The first message
 * per field wins so the UI shows one inline error per field.
 */
export function mapErrorsToFields(
  errors: ServerError[],
  knownKeys: Set<string>,
): { fieldErrors: Record<string, string>; formErrors: ServerError[] } {
  const fieldErrors: Record<string, string> = {};
  const formErrors: ServerError[] = [];
  for (const err of errors) {
    const directField = err.field && knownKeys.has(err.field) ? err.field : null;
    const key = directField ?? parseFieldKeyFromErrorCode(err.errorCode, knownKeys);
    if (key) {
      if (fieldErrors[key] === undefined) {
        fieldErrors[key] = err.message || err.errorCode || 'Invalid value';
      }
    } else {
      formErrors.push(err);
    }
  }
  return { fieldErrors, formErrors };
}
